/** Client-side mirror of the world. Nothing here is authoritative. */

import { MAP_W, MAP_H, Phase, RES_COUNT, TILE_COUNT } from '../shared/constants';

export interface NetPlayer {
  id: number;
  name: string;
  team: number;
  color: number;
  bot: boolean;
  kind: number;          // 0 human · 1 AI kingdom · 2 monster camp
  race: number;          // 0 human · 1 dwarf · 2 elf
  tiles: number;
  troops: number;
  troopCap: number;
  troopRate: number;     // soldiers per second
  /** wall segments standing — ground under one counts towards nobody's victory */
  wallCount: number;
  eliminated: boolean;
  connected: boolean;
  protectedNow: boolean;
  sumX: number;
  sumY: number;
  count: number;
}

export interface ClientUnit {
  id: number;
  type: number;
  owner: number;
  /** transports only, and only yours and your allies': troops aboard */
  cargo: number;
  px: number; py: number;   // interpolation start
  tx: number; ty: number;   // interpolation target
  t0: number;
  hp: number;
  flags: number;
  stamp: number;
}

export interface ClientStruct {
  id: number;
  type: number;
  owner: number;
  tile: number;
  x: number; y: number;
  hp: number;
  queue: number;
  level: number;
  /** 0..1 construction progress; 1 means finished and working */
  built: number;
  jobs: { type: number; progress: number }[];
  stamp: number;
}

/**
 * One wall segment, as it arrives from the server. Keyed by tile, because that
 * is what a wall is: a property of the ground, not an entity standing on it.
 */
export interface ClientWall {
  tile: number;
  /** WallKind: 1 rampart, 2 gate */
  kind: number;
  owner: number;
  /** 0..255 of its own maximum */
  hp: number;
}

export interface PingMark {
  x: number; y: number; kind: number; color: number; born: number;
}

/** What an ally lets you see of their books. */
export interface AllyBooks {
  res: number[];
  food: number;      // net food income, so you can spot a starving friend
  markets: number;   // no market, nowhere to unload a relief caravan
}

/** One contested border: who is pushing, who is holding, and with how many. */
export interface Front {
  x: number; y: number;
  attacker: number; defender: number;
  atkTroops: number; defTroops: number;
  /** radians: which way the spearhead is rolling */
  heading: number;
}

export const state = {
  connected: false,
  you: 0,
  roomId: '',
  phase: Phase.Lobby as Phase,
  countdown: 0,
  seed: 0,
  /**
   * The played rectangle of this match. The grid is always `MAP_W x MAP_H` —
   * that is the stride of every array — but the world inside it is sized to the
   * roster, and the camera and the terrain canvas both follow this.
   */
  worldW: MAP_W,
  worldH: MAP_H,

  terrain: null as Uint8Array | null,
  /** 1 where a river runs — water that belongs to nobody. Regenerated from the seed. */
  river: null as Uint8Array | null,
  owner: new Uint16Array(TILE_COUNT),

  players: new Map<number, NetPlayer>(),
  /**
   * How the war stands, totalled by the server that decides it.
   *
   * Per-kingdom stats now only arrive for the kingdoms on screen, so the client
   * can no longer add up who holds what — and it should not have been doing so
   * anyway. These are the very numbers `checkVictory` runs on: land that counts
   * towards a win (wilderness and everything under stonework written off), and
   * each team's share of it.
   */
  teamLand: new Map<number, number>(),
  contestedLand: 1,
  units: new Map<number, ClientUnit>(),
  structures: new Map<number, ClientStruct>(),
  /** the wall layer in view, keyed by tile, replaced wholesale at 2 Hz */
  walls: new Map<number, ClientWall>(),
  /** bumped every time the wall layer is replaced, so it is redrawn */
  wallRevision: 0,

  self: {
    troops: 0,
    committed: 0,
    cap: 0,
    res: new Float64Array(RES_COUNT),
    income: new Float64Array(RES_COUNT),
    starving: false,
    troopRate: 0,
    villageBonus: 0,
    foodFactor: 1,
    armyCap: 3,
    /** wall segments standing, which is what prices the next one */
    wallCount: 0,
    /** the two standing orders, as the server sees them */
    repairing: false,
    defending: false,
    /** how many times the race power has been called; each one is dearer */
    powerUses: 0,
    /**
     * Where your realm's weight sits, as the server computes it.
     *
     * The race power's reach is measured from here and nowhere else, so this is
     * what lets the client refuse an impossible shot before you spend on it.
     */
    centroidX: 0,
    centroidY: 0,
  },

  /**
   * Every kingdom you have exchanged blows with, as the server sees it.
   *
   * Not decoration: your soldiers will not lift a finger against anybody who is
   * not in here, so this set is the difference between an army and an ornament.
   * Pushed by the server when it grows; it never shrinks.
   */
  atWar: new Set<number>(),
  /** the books of your team-mates, refreshed at 2 Hz */
  allyBooks: new Map<number, AllyBooks>(),
  /** live frontlines in view, for the numbers painted on contested borders */
  fronts: [] as Front[],
  /**
   * Every attack **you** have running — target realm and troops committed.
   *
   * Distinct from `fronts`, which is whoever's fighting happens to be on
   * screen. This is your own list of open commitments, and each one can be
   * called off from the bar under the slider.
   */
  attacks: [] as { target: number; troops: number }[],
  /**
   * Race powers falling somewhere on the map right now — anybody's.
   *
   * Drawn as a ring in the caster's colour with both names on it, so the single
   * loudest event in the game is never anonymous craters appearing in silence.
   */
  powers: [] as { x: number; y: number; owner: number; realm: number; area: number }[],
  /**
   * How wide a bite the power will take when you fire it, in tiles.
   *
   * Set with the scroll wheel while the power is armed. The number of shots
   * never changes — see `RACE_POWER_AREA_MIN`.
   */
  powerRadius: 12,

  selected: new Set<number>(),
  /**
   * One of your Markets, picked up so its trade route can be set, or -1.
   *
   * Separate from `selected` on purpose: that is a set of *units* and is what
   * right-click movement reads. A market is a different kind of thing to have
   * in hand — it never moves, and the only order it takes is "sell to them".
   */
  selectedMarket: -1,
  ratio: 0.3,
  buildType: -1,
  /** unit type waiting for you to click its destination (openfront boat flow) */
  trainType: -1,
  boatMode: false,
  /**
   * Laying wall: `WallKind.Rampart`, `WallKind.Gate`, or 0 for not building
   * one. Walls are drawn by dragging the mouse, so this is a mode rather than a
   * click target — see `input.ts`.
   */
  wallMode: 0,
  /** the tiles the current wall drag has crossed, in the order it crossed them */
  wallDrag: [] as number[],
  /** the race power is armed and waiting for you to click a target */
  powerMode: false,
  hoverTile: -1,
  /** last cursor position, and whether it is over the world rather than the HUD */
  cursorX: 0,
  cursorY: 0,
  overWorld: false,
  /**
   * How much of this world is land, from the client's own copy of the map.
   *
   * Not what victory is measured against — that is `contestedLand`, which
   * writes off the wilderness and everything under stonework, and comes from
   * the server. This is only the raw shape of the world.
   */
  landTiles: 1,

  dirty: new Set<number>(),
  fullRedraw: true,
  pings: [] as PingMark[],

  lobby: {
    humans: 0, max: 128, teams: 2, perTeam: 1, aiKingdoms: 0, camps: 0,
    mode: 1, modeName: 'Teams', worldW: MAP_W, worldH: MAP_H,
    light: false,
    manualStart: false, hostId: 0,
  },
  /** the people you picked in the menu, and may still change in the lobby */
  race: Number(localStorage.getItem('kaelmoor.race') || 0),

  /**
   * **Which edition you are in** — the menu's Light / Full switch.
   *
   * Decides which worlds the browser shows you, which one Quickplay drops you
   * into, and what the build palette and the HUD contain. A first visit lands
   * on Light: the full game asks a new player to learn three peoples, five
   * resources and four military halls before they have finished their first
   * border, and every one of those is content rather than rules.
   *
   * Remembered, so somebody who has moved on to the full game stays there.
   * Once a match starts `lobby.light` is what actually governs, because the
   * world decides its own edition and this is only what you *asked* for.
   */
  light: localStorage.getItem('kaelmoor.light') !== '0',

  stamp: 0,
  lastEntityAt: 0,
  tickInterval: 100,
  gameOver: null as null | { standings: { team: number; tiles: number; name: string }[]; winner: number },

  /** the largest your realm ever was, for the defeat screen */
  peakTiles: 0,
  /** your kingdom has fallen — the war goes on without you */
  defeated: false,
  /** you dismissed the defeat screen and are watching the rest of the war */
  spectating: false,
};

export function you(): NetPlayer | undefined {
  return state.players.get(state.you);
}

/**
 * There are only three relationships left: you, your team, and everybody else.
 * Alliances were removed — see `Game.areAllied` on the server.
 */
export function relationColorTag(id: number): 'self' | 'team' | 'enemy' {
  if (id === state.you) return 'self';
  const me = you();
  const other = state.players.get(id);
  if (me && other && me.team >= 0 && me.team === other.team) return 'team';
  return 'enemy';
}

/** Have you and this kingdom ever come to blows? Drives the crossed swords. */
export function atWarWith(id: number): boolean {
  return id !== 0 && id !== state.you && state.atWar.has(id);
}

export function isFriendly(id: number): boolean {
  if (id === 0) return false;
  const tag = relationColorTag(id);
  return tag !== 'enemy';
}

/** Applies one ownership change and keeps derived data in sync. */
export function applyOwner(tile: number, newOwner: number) {
  const old = state.owner[tile];
  if (old === newOwner) return;
  const x = tile % MAP_W, y = (tile / MAP_W) | 0;
  if (old !== 0) {
    const p = state.players.get(old);
    if (p) { p.count--; p.sumX -= x; p.sumY -= y; }
  }
  state.owner[tile] = newOwner;
  if (newOwner !== 0) {
    const p = state.players.get(newOwner);
    if (p) { p.count++; p.sumX += x; p.sumY += y; }
  }
  markDirty(tile);
}

export function markDirty(tile: number) {
  const x = tile % MAP_W, y = (tile / MAP_W) | 0;
  state.dirty.add(tile);
  if (x > 0) state.dirty.add(tile - 1);
  if (x < MAP_W - 1) state.dirty.add(tile + 1);
  if (y > 0) state.dirty.add(tile - MAP_W);
  if (y < MAP_H - 1) state.dirty.add(tile + MAP_W);
}

export function resetWorld() {
  state.owner.fill(0);
  state.units.clear();
  state.structures.clear();
  state.walls.clear();
  state.wallRevision++;
  state.wallMode = 0;
  state.wallDrag.length = 0;
  state.powerMode = false;
  state.selected.clear();
  state.selectedMarket = -1;
  state.pings.length = 0;
  state.dirty.clear();
  state.fullRedraw = true;
  state.gameOver = null;
  state.peakTiles = 0;
  state.defeated = false;
  state.spectating = false;
  state.allyBooks.clear();
  state.atWar.clear();
  state.fronts.length = 0;
  state.attacks.length = 0;
  state.powers.length = 0;
  state.teamLand.clear();
  state.contestedLand = 1;
  /*
   * Your realm's weight belongs to the world that computed it.
   *
   * Left standing, it is a live-looking answer to "where is my kingdom" from a
   * war that finished — which the camera reads when it goes home at the start
   * of a match, and would have flown to a spot on the new map that means
   * nothing. Zero is the correct "nobody knows yet", and the next snapshot is
   * a tenth of a second away.
   */
  state.self.centroidX = 0;
  state.self.centroidY = 0;
  /*
   * Nobody has fallen in a war that has not started.
   *
   * `eliminated` used to survive into the next match — the snapshot that
   * corrects it is a tick or two behind the `match` message — and anything
   * asking "is this player still in it" during that gap got last week's answer.
   * The camera is one such thing: it reads this to decide between flying you
   * home and leaving a dead player the whole map, and a stale `true` cost the
   * winner of one war the opening shot of the next.
   */
  for (const p of state.players.values()) {
    p.count = 0; p.sumX = 0; p.sumY = 0; p.tiles = 0;
    p.eliminated = false;
  }
}
